import { CalendarClock, Flag } from 'lucide-react'
import { Link } from 'react-router-dom'

import { useI18n } from '@/shared/i18n'

import type { PortalProjectListItem } from '../types'

import { PhaseBadge } from './PhaseBadge'
import { PhaseTrack } from './PhaseTrack'

export interface PortalProjectCardProps {
  project: PortalProjectListItem
}

/**
 * La tarjeta grande del portal.
 *
 * Es la diferencia de densidad más visible con el interno, y es deliberada:
 * acá no hay uso diario ni catorce filas que comparar de un vistazo. Hay cuatro
 * cosas que un cliente quiere saber de cada proyecto —cómo se llama, en qué
 * fase está, qué sigue y para cuándo— y entran todas sin tener que abrir nada.
 *
 * **Sin espina de empresa**: el cliente ve una sola empresa, así que el color
 * no distingue nada y sería ruido. **Sin orden interno**: en qué orden
 * atendemos a nuestros clientes no es asunto de un cliente.
 */
export function PortalProjectCard({ project }: PortalProjectCardProps) {
  const { t, formatDate } = useI18n()

  return (
    <Link
      to={`/portal/projects/${project.id}`}
      className="hairline flex flex-col gap-3 rounded-[var(--radius-card)] bg-surface-1 px-4 py-4 transition-colors duration-[var(--motion-fast)] hover:bg-surface-2"
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        {/* El nombre lo escribió el equipo: viaja tal cual. */}
        <h3 className="font-display text-md text-text-1">{project.name}</h3>
        <PhaseBadge
          label={project.stage_label}
          tone={project.stage_tone}
          isOngoing={project.is_ongoing}
        />
      </div>

      <dl className="flex flex-col gap-1.5 text-xs">
        <div className="flex items-start gap-2">
          <dt className="flex shrink-0 items-center gap-1.5 text-text-3">
            <Flag size={13} aria-hidden="true" />
            {t('portal.card.nextMilestone')}
          </dt>
          <dd className="min-w-0 flex-1 text-text-1">
            {/* El hito es el título de una tarea: texto de usuario, sin traducir. */}
            {project.next_milestone ?? (
              <span className="text-text-3">{t('portal.card.milestoneTbd')}</span>
            )}
          </dd>
        </div>

        <div className="flex items-start gap-2">
          <dt className="flex shrink-0 items-center gap-1.5 text-text-3">
            <CalendarClock size={13} aria-hidden="true" />
            {t('portal.detail.targetDate')}
          </dt>
          <dd className="num min-w-0 flex-1 text-text-1">
            {project.target_end_date ? (
              formatDate(project.target_end_date)
            ) : (
              <span className="text-text-3">{t('portal.card.noTargetDate')}</span>
            )}
          </dd>
        </div>
      </dl>

      {project.is_ongoing ? (
        <p className="text-2xs text-text-3">{t('portal.card.ongoingNote')}</p>
      ) : (
        <PhaseTrack label={project.stage_label} tone={project.stage_tone} />
      )}
    </Link>
  )
}
